<template>
  <dara-single-content-layout container-size="lg">
    <template v-if="user" #content>
      <dara-row justify="center" class="mb-5 d-flex align-items--center">
        <dara-col :xs="24" :span="7" class="d-flex align-items--center direction--column justify-content-center">
          <div>
            <dara-user-badge v-if="badge" :badge="badge" :size="70" offset-y="14" offset-x="3.5" />
            <dara-image
              fit="cover"
              :src="profilePic"
              :height="200"
              :width="200"
              class="p--lg el-avatar--circle" />
          </div>

          <div v-if="!user.blocked" class="d-flex align-items--center direction--column justify-content-center">
            <dara-header size="sm" style="padding-top: 2px">
              {{ followers }}
            </dara-header>
            <div v-if="currentUser.id != user.id" class="pt-2">
              <dara-button
                v-if="!following"
                class="btn btn-success"
                size="sm"
                @click="follow()">
                {{ $t('follow') }}
              </dara-button>
              <dara-button
                v-if="following"
                class="btn btn-danger"
                size="sm"
                @click="unfollow()">
                {{ $t('unfollow') }}
              </dara-button>
            </div>
          </div>
          <div v-else class="d-flex align-items--center direction--column justify-content-center">
            <dara-p color="info" class="mb-2">
              {{ $t('blocked') }}
            </dara-p>
          </div>
        </dara-col>
        <dara-col :xs="24" :span="17" justify="left">
          <dara-header size="md" style="margin-bottom: -3px">
            {{ user.name }} {{ user.last_name }}
          </dara-header>
          <dara-small class="pb-5">
            {{ permission }}
          </dara-small>
          <dara-p v-if="user.biography" size="md" class="dara-u-text--medium mt-2">
            {{ user.biography }}
          </dara-p>
          <div>
            <span v-if="user.isBestWriter" class="badge rounded-pill bg-primary text-light mt-3 me-2">
              {{ $t('best-writer') }}
            </span>
            <span v-if="user.isTopWriter" class="badge rounded-pill bg-primary text-light mt-3 me-2">
              {{ $t('top-writer') }}
            </span>
            <span v-if="user.isTopReader" class="badge rounded-pill bg-primary text-light mt-3 me-2">
              {{ $t('top-reader') }}
            </span>
          </div>
          <dara-user-view-information :user="user" />
        </dara-col>
      </dara-row>

      <dara-user-tabs class="mt-5" :user="user" />
    </template>
  </dara-single-content-layout>
</template>

<script>
import { getters } from '@/modules/user/user.store';
import { toastError } from '@/services/toastService';
import { fetchUserArticles } from '@/modules/article/article.service';
import {
  updateUserVisits,
  fetchBestWriters,
  fetchTopReaders,
  fetchTopWriters,
  getUserById,
  getFollowingStatus,
  getFollowers,
  followUser,
  unfollowUser,
} from '@/modules/user/user.service';
import { getImageFromAssets } from '@/helpers/images';
import { NO_ARTICLE_IMAGE } from '@/modules/article/article.constants';
import { USERS_ROLES } from '@/modules/user/user.constants';
import { translate } from '@/services/translate.service';
import { formatToTimeAgo } from '@/helpers/date';

import DaraUserBadge from '@/modules/user/components/UserBadge.vue';
import DaraUserViewInformation from '@/modules/user/components/UserViewComponent.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';
import DaraUserTabs from '@/modules/user/components/UserTabs.vue';

export default {
  name: 'DaraUserViewPage',
  components: {
    DaraUserBadge,
    DaraUserViewInformation,
    DaraSingleContentLayout,
    DaraUserTabs,
  },
  data() {
    return {
      following: false,
      user: null,
      numberFollowers: null,
      permission: null,
      profilePic: getImageFromAssets(NO_ARTICLE_IMAGE),
      articles: null,
      questions: null,
      currentUser: getters.getUser(),
      pagination: {
        page: 1,
        limit: 5,
      },
    };
  },
  computed: {
    user_id() {
      return this.$route.params?.id;
    },
    followers() {
      if (this.numberFollowers > 1) {
        return `${this.numberFollowers} ${translate('followers')}`;
      }
      return `${this.numberFollowers} ${translate('follower')}`;
    },
    badge() {
      if (this.user.isBestWriter) return 'best-writer';
      if (this.user.isTopWriter) return 'top-writer';
      if (this.user.isTopReader) return 'top-reader';
      return false;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    fetch() {
      getUserById(this.user_id)
        .then(data => {
          this.user = data;
          if (this.user.profile_pic) {
            this.profilePic = this.user.profile_pic;
          }
          this.permission = `${translate(USERS_ROLES[this.user.permission])}`;
          this.fetchFeaturedUsers();
          this.getFollowers();
          this.user.visits++;
          updateUserVisits(this.user);
        })
        .catch(() => toastError(`${translate('error-on-getting-user-by-id')}`));
    },
    fetchFeaturedUsers() {
      this.fetchUserArticles();
      this.fetchBestWriters();
      this.fetchTopWriters();
      this.fetchTopReaders();
    },
    getFollowers() {
      getFollowers(this.user)
        .then(response => {
          this.numberFollowers = response.length;
          this.getFollowingStatus();
        })
        .catch(() => toastError(`${translate('error-on-getting-followers')}`));
    },
    fetchUserArticles() {
      fetchUserArticles(this.user, this.pagination)
        .then(data => {
          this.articles = data.data;
          Object.assign(this.pagination, data.pagination);
        })
        .catch(() => toastError(`${translate('error-on-getting-users-articles')}`));
    },
    fetchBestWriters() {
      fetchBestWriters(1)
        .then(data => {
          if (data.id === this.user_id) {
            this.$set(this.user, 'isBestWriter', true);
          }
        });
    },
    fetchTopReaders() {
      fetchTopReaders(1)
        .then(data => {
          if (data.id === this.user_id) {
            this.$set(this.user, 'isTopReader', true);
          }
        });
    },
    fetchTopWriters() {
      fetchTopWriters(1)
        .then(data => {
          if (data.id === this.user_id) {
            this.$set(this.user, 'isTopWriter', true);
          }
        });
    },
    getFollowingStatus() {
      getFollowingStatus(this.user)
        .then(response => {
          this.following = response;
        })
        .catch(() => toastError(`${translate('error-getting-the-following-status')}`));
    },
    follow() {
      followUser(this.user)
        .then(() => {
          this.following = true;
          this.getFollowers();
        })
        .catch(() => toastError(`${translate('error-on-following-user')}`));
    },
    unfollow() {
      unfollowUser(this.user)
        .then(() => {
          this.following = false;
          this.getFollowers();
        })
        .catch(() => toastError(`${translate('error-on-unfollowing-user')}`));
    },
    formatToTimeAgo,
  },
};
</script>
